import React from 'react'
import { RecipeRecord } from '@talktoast/shared'

interface ArchiveRecipeModalProps {
  recipe: RecipeRecord | null
  isOpen: boolean
  onClose: () => void
  onArchive: (recipeId: string) => void
  onArchiveAsCooked: (recipeId: string) => void
}

export const ArchiveRecipeModal: React.FC<ArchiveRecipeModalProps> = ({
  recipe,
  isOpen,
  onClose,
  onArchive,
  onArchiveAsCooked
}) => {
  if (!isOpen || !recipe) return null

  const recipeData = recipe.recipe_data

  // Calculate days since recipe was created
  const createdAt = new Date(recipe.created_at)
  const now = new Date()
  const daysSince = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24))

  const handleCookedClick = () => {
    onArchiveAsCooked(recipe.id)
    onClose()
  }

  const handleDidNotCookClick = () => {
    onArchive(recipe.id)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-md w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">
                What happened with this meal?
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {recipeData.name}
              </p>
              {daysSince > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Added {daysSince} {daysSince === 1 ? 'day' : 'days'} ago
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-3">
          {/* Option 1: I cooked this */}
          <button
            onClick={handleCookedClick}
            className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl group-hover:scale-110 transition-transform">✅</span>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 group-hover:text-green-700">
                  I cooked this
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Rate this recipe
                </p>
              </div>
            </div>
          </button>

          {/* Option 2: I didn't cook this */}
          <button
            onClick={handleDidNotCookClick}
            className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all text-left group"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl group-hover:scale-110 transition-transform">❌</span>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 group-hover:text-red-700">
                  I didn't cook this
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Remove from my meal plan
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            Cancel - Keep it here
          </button>
        </div>
      </div>
    </div>
  )
}
